import { useDebounce } from '@/hooks'
import { useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import type { PaginationMeta } from '@/types/api.types'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TOOLBAR
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export interface DataTableToolbarProps {
  onSearch?: (value: string) => void
  searchPlaceholder?: string
  actions?: React.ReactNode
  filters?: React.ReactNode
}

export function DataTableToolbar({
  onSearch,
  searchPlaceholder = 'Rechercher...',
  actions,
  filters,
}: DataTableToolbarProps) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)

  useEffect(() => {
    onSearch?.(debouncedSearch)
  }, [debouncedSearch, onSearch])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {onSearch && (
          <div style={{ position: 'relative', flex: '1 1 0', maxWidth: 280 }}>
            <Search
              size={13}
              style={{
                position: 'absolute', left: 9, top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--tx4)', pointerEvents: 'none',
              }}
            />
            <input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                height: 32,
                paddingLeft: 28, paddingRight: 10,
                border: '0.5px solid var(--bd)',
                borderRadius: 8,
                background: '#fff',
                fontSize: 13,
                fontFamily: '"Raleway", sans-serif',
                color: 'var(--tx)',
                outline: 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--blue)'
                e.target.style.boxShadow = '0 0 0 3px rgba(245,124,0,0.10)'
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--bd)'
                e.target.style.boxShadow = 'none'
              }}
            />
          </div>
        )}
        <div style={{ flex: 1 }} />
        {actions && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {actions}
          </div>
        )}
      </div>
      {filters && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {filters}
        </div>
      )}
    </div>
  )
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SERVER PAGINATION — compact, Vercel-style
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export interface DataTableServerPaginationProps {
  meta: PaginationMeta
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  isLoading?: boolean
}

const btnStyle = (disabled: boolean): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28,
  borderRadius: 6,
  border: '0.5px solid var(--bd)',
  background: '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer',
  color: disabled ? 'var(--tx4)' : 'var(--tx2)',
  fontSize: 13,
  fontFamily: '"Raleway", sans-serif',
  opacity: disabled ? 0.4 : 1,
  transition: 'background 0.1s, border-color 0.1s',
})

export function DataTableServerPagination({
  meta,
  onPageChange,
  onLimitChange,
  isLoading,
}: DataTableServerPaginationProps) {
  if (!meta || meta.total === undefined) return null
  const { total, page, limit, totalPages, hasNextPage, hasPreviousPage } = meta

  const start = (page - 1) * limit + 1
  const end = Math.min(page * limit, total)

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '9px 14px',
      borderTop: '0.5px solid var(--bd)',
    }}>
      {/* Left: count + rows per page */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <p style={{ fontSize: 12, color: 'var(--tx3)', fontFamily: '"Raleway", sans-serif' }}>
          {total === 0 ? 'Aucun résultat' : `${start}–${end} sur ${total}`}
        </p>
        <select
          value={limit}
          onChange={(e) => { onLimitChange(Number(e.target.value)); onPageChange(1) }}
          disabled={isLoading}
          style={{
            height: 26,
            padding: '0 8px',
            border: '0.5px solid var(--bd)',
            borderRadius: 6,
            background: '#fff',
            color: 'var(--tx2)',
            fontSize: 12,
            fontFamily: '"Raleway", sans-serif',
            outline: 'none',
            cursor: 'pointer',
          }}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>

      {/* Right: page controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          onClick={() => onPageChange(1)}
          disabled={!hasPreviousPage || !!isLoading}
          style={btnStyle(!hasPreviousPage || !!isLoading)}
          title="Première page"
        >
          «
        </button>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPreviousPage || !!isLoading}
          style={btnStyle(!hasPreviousPage || !!isLoading)}
          title="Page précédente"
        >
          ‹
        </button>

        <span style={{
          padding: '0 10px',
          fontSize: 12,
          color: 'var(--tx3)',
          fontFamily: '"Raleway", sans-serif',
        }}>
          {page} / {totalPages}
        </span>

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNextPage || !!isLoading}
          style={btnStyle(!hasNextPage || !!isLoading)}
          title="Page suivante"
        >
          ›
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={!hasNextPage || !!isLoading}
          style={btnStyle(!hasNextPage || !!isLoading)}
          title="Dernière page"
        >
          »
        </button>
      </div>
    </div>
  )
}
